/**
 * createAgent — Frontend SDK for AI agent with tool loop.
 * Works in both browser (component-server) and Deno (CODE_EDITOR_V2).
 * Pure fetch, no dependencies.
 *
 * Backend proxy handles: cross-provider ID normalization, dedup,
 * orphan repair, provider-specific formatting, credit deduction.
 * Frontend handles: tool loop execution + loop detection.
 */

const MAX_TOOL_ROUNDS = 200;
const MAX_CONSECUTIVE_IDENTICAL = 3;

// ==================== SHARED HELPERS ====================

function _getApiUrl() {
    const dbProxy = typeof window !== "undefined"
        ? window.process?.env?.DATABASE_PROXY
        : (typeof Deno !== "undefined" ? Deno.env.get("DATABASE_PROXY") : null);
    if (dbProxy) return new URL(dbProxy).origin;
    return "https://api.agentui.ai";
}

function _getApiKey() {
    if (typeof window !== "undefined") return window.config?.apiKey;
    if (typeof Deno !== "undefined") return Deno.env.get("API_KEY");
    return null;
}

async function _callProxyRaw(path, body) {
    const apiKey = _getApiKey();
    if (!apiKey) throw new Error("No API key configured");

    const res = await fetch(`${_getApiUrl()}/proxy/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": apiKey },
        body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.error) {
        // Tag the origin. This function RETHROWS the backend's error string, so
        // an unlabelled message lands in the app's stack trace pointing at
        // _callProxyRaw and reads like an SDK/app bug — a customer once spent
        // several debugging rounds auditing their own code for a TypeError that
        // was thrown server-side. The prefix makes the origin unambiguous to
        // both the developer and the AI reading the console.
        const detail = json.error || `Request failed: ${res.status}`;
        const err = new Error(
            `[AgentUI ${path} — server responded ${res.status}] ${detail}`
        );
        err.status = res.status;
        err.serverError = detail;
        err.proxyPath = path;
        throw err;
    }
    return json.data || json;
}

// ==================== PLATFORM TOOLS ====================

const tools = {
    /**
     * Web search tool — uses Perplexity AI via the backend.
     * Credits are deducted automatically per search.
     */
    searchWeb() {
        return {
            name: "search_web",
            description: "Search the web for up-to-date information. Returns an answer with sources. Use this when you need current data, facts, documentation, or anything not in your training data.",
            parameters: {
                type: "object",
                properties: {
                    query: { type: "string", description: "The search query — be specific for best results" },
                },
                required: ["query"],
            },
            handler: async ({ query }) => {
                const result = await _callProxyRaw("langchain/base-agent/web-search", {
                    action: "web-search",
                    query,
                });
                return { answer: result.answer, sources: result.sources };
            },
        };
    },

    /**
     * Image generation tool — uses DALL-E, GPT Image, or Gemini via the backend.
     * Credits are deducted automatically per image.
     */
    generateImage(options = {}) {
        const defaultModel = options.model || "gemini-3.1-flash-image-preview";
        const defaultSize = options.size || "1024x1024";
        const defaultQuality = options.quality || "standard";
        return {
            name: "generate_image",
            description: "Generate an image from a text description. Returns a URL to the generated image. Use this when the user asks to create, draw, design, or visualize something.",
            parameters: {
                type: "object",
                properties: {
                    prompt: { type: "string", description: "Detailed description of the image to generate" },
                    model: { type: "string", description: `Image model to use (default: ${defaultModel}). Options: dall-e-3, dall-e-2, gpt-image-1, gpt-image-2, nano-banana, nano-banana-pro, gemini-3.1-flash-image-preview, flux-pro, flux-2-pro` },
                    size: { type: "string", description: `Image size (default: ${defaultSize}). Options: 1024x1024, 1024x1792, 1792x1024` },
                    quality: { type: "string", description: `Image quality (default: ${defaultQuality}). Options: standard, hd` },
                },
                required: ["prompt"],
            },
            handler: async ({ prompt, model, size, quality }) => {
                const body = {
                    prompt,
                    model: model || defaultModel,
                    size: size || defaultSize,
                };
                const q = quality || defaultQuality;
                if (q) body.quality = q;
                const result = await _callProxyRaw("langchain/generate-image", body);
                const images = result.images || result.data?.images || [];
                if (!images.length) return { error: "No image was generated" };
                return {
                    images: images.map(img => ({ url: img.url, revisedPrompt: img.revisedPrompt })),
                    model: result.model || model || defaultModel,
                };
            },
        };
    },
};

// ==================== MAIN SDK ====================

function createAgent({ provider, model, tools: userTools = [], system, temperature, maxTokens, integrationId }) {
    const handlerMap = {};
    const toolDefs = userTools.map(t => {
        handlerMap[t.name] = t.handler;
        return { name: t.name, description: t.description, parameters: t.parameters };
    });

    async function callProxy(action, body) {
        return _callProxyRaw(`langchain/base-agent/${action}`, body);
    }

    // Loop detection — ported from BaseAgent._detectToolCallLoop()
    function createSignatures(toolCalls) {
        return toolCalls.map(tc => {
            let args;
            try { args = JSON.parse(tc.arguments || "{}"); } catch { args = {}; }
            const sortedArgs = JSON.stringify(args, Object.keys(args).sort());
            return `${tc.name}:${sortedArgs}`;
        }).sort();
    }

    function signaturesMatch(a, b) {
        if (!a || !b || a.length !== b.length) return false;
        return a.every((sig, i) => sig === b[i]);
    }

    return {
        async chat(userMessages, options = {}) {
            let messages = typeof userMessages === "string"
                ? [{ role: "user", content: userMessages }]
                : [...userMessages];

            const onToolCall = options.onToolCall || null;
            const onToolResult = options.onToolResult || null;
            const onThinking = options.onThinking || null;

            let previousSignatures = null;
            let consecutiveIdentical = 0;

            for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
                const result = await callProxy("chat", {
                    action: "chat", provider, model, messages,
                    system: options.system || system,
                    tools: toolDefs.length ? toolDefs : undefined,
                    temperature: options.temperature ?? temperature,
                    maxTokens: options.maxTokens ?? maxTokens,
                    integrationId: options.integrationId ?? integrationId,
                });

                if (!result.toolCalls?.length) return result;

                // Fire onThinking if the model produced text alongside tool calls
                if (onThinking && result.content) {
                    try { onThinking({ content: result.content, round }); } catch {}
                }

                // Loop detection — identical tool calls across rounds
                const currentSignatures = createSignatures(result.toolCalls);
                if (signaturesMatch(currentSignatures, previousSignatures)) {
                    consecutiveIdentical++;
                    if (consecutiveIdentical >= MAX_CONSECUTIVE_IDENTICAL) {
                        return {
                            ...result,
                            content: result.content || `Agent stopped: identical tool calls repeated ${consecutiveIdentical} times`,
                            toolCalls: [],
                            _loopDetected: true,
                        };
                    }
                } else {
                    consecutiveIdentical = 1;
                }
                previousSignatures = currentSignatures;

                // Append assistant message — IDs already normalized by backend
                messages.push({
                    role: "assistant", content: result.content || "",
                    tool_calls: result.toolCalls.map(tc => ({
                        id: tc.id, name: tc.name, arguments: tc.arguments,
                    })),
                });

                // Execute each tool handler locally, append results
                for (const tc of result.toolCalls) {
                    const handler = handlerMap[tc.name];
                    if (!handler) throw new Error(`No handler for tool "${tc.name}"`);
                    let args;
                    try { args = JSON.parse(tc.arguments); } catch { args = {}; }

                    // Fire onToolCall before execution
                    if (onToolCall) {
                        try { onToolCall({ name: tc.name, args, round }); } catch {}
                    }

                    let toolResult;
                    try {
                        toolResult = await handler(args);
                    } catch (err) {
                        toolResult = { error: err.message };
                    }

                    // Fire onToolResult after execution
                    if (onToolResult) {
                        try { onToolResult({ name: tc.name, args, result: toolResult, round }); } catch {}
                    }

                    messages.push({
                        role: "tool", tool_call_id: tc.id,
                        content: typeof toolResult === "string" ? toolResult : JSON.stringify(toolResult),
                    });
                }
            }
            throw new Error(`Tool loop exceeded ${MAX_TOOL_ROUNDS} rounds`);
        },

        async embed(texts, options = {}) {
            return callProxy("embed", {
                action: "embed", provider, model,
                texts, integrationId: options.integrationId ?? integrationId,
            });
        },
    };
}

createAgent.tools = tools;

// MAX_TOOL_ROUNDS is exported for the contract test: it hardcoded 10 and went
// stale when the cap moved to 200 (e899e2846), so the suite failed for months
// on a deliberate change. Importing the real value makes that impossible.
export { createAgent, MAX_TOOL_ROUNDS };
